import React from 'react';

import { useClassName } from 'common/hooks';
import { ContentfulRichText } from 'common/components';
import HomepageAsset from '../Asset';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';

import HomepageImage from '../../../Home/Components/Image';
import Actions from '../Actions';
import Link from '../Link';
import Labels from '../Labels';
import CardTypeL from './CardTypeL';
import CardTypeM from './CardTypeM';
import CardTypeN from './CardTypeN';
import CardTypeS from './CardTypeS';
import CardTypeV from './CardTypeV';
import InfoModal from '../InfoModal';
import useCustomStyle from '../../hooks';
import { getStylesByPrefix, parseVideoSettings } from '../../utils';

import './card.less';
import CardTypeU, { CardInfoModal } from './CardTypeU';

function Badge({ children }) {
  const [setRef] = useCustomStyle(children.fields.settings);

  return (
    <li ref={setRef} key={children.sys.id}>
      {children.fields.label}
    </li>
  );
}

export function CardTypeA({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeA');
  const [setRef, styles] = useCustomStyle(item.fields.settings);

  const linkHref = styles['--link-href'];
  const titleStyles = getStylesByPrefix('--title-', styles);
  const subtitleStyles = getStylesByPrefix('--subtitle-', styles);
  const containerStyle = getStylesByPrefix('--container-', styles);
  const Container = linkHref ? 'a' : 'div';
  const containerProps = linkHref
    ? { href: linkHref, onClick: (e) => e.stopPropagation() }
    : {};

  const videoPlayOnHover = styles['--video-play-on-hover'] === 'true';
  const videoStopOnLeave = styles['--video-stop-on-leave'] === 'true';
  const videoPauseOnLeave = styles['--video-pause-on-leave'] === 'true';
  const videoNoAutoplay = styles['--video-no-autoplay'] === 'true';
  const videoOpacity = styles['--video-opacity'];

  return (
    <Container
      className={classNames('container')}
      ref={setRef}
      {...containerProps}
      style={containerStyle}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
        videoStyle={videoOpacity ? { opacity: videoOpacity } : undefined}
        videoEvents={{
          playOnHover: videoPlayOnHover,
          stopOnLeave: videoStopOnLeave,
          pauseOnLeave: videoPauseOnLeave,
          autoplay: !videoNoAutoplay,
        }}
        videoProps={parseVideoSettings(styles)}
      />
      {Boolean(
        item?.fields?.subtitle ||
          item?.fields?.title ||
          item?.fields?.links?.length ||
          item?.fields?.infoModal
      ) && (
        <div
          {...(styles?.['--orientation'] === 'horizontal' && {
            className: classNames('horizontal'),
          })}>
          {item.fields.subtitle && (
            <span
              className={
                styles['--subtitle-lines-overflow']
                  ? classNames('subtitle-ellipsis')
                  : undefined
              }
              style={{
                ...(styles['--subtitle-lines-overflow'] && {
                  WebkitLineClamp: styles['--subtitle-lines-overflow'],
                }),
                ...subtitleStyles,
              }}>
              {item.fields.subtitle}
            </span>
          )}
          <h4
            className={
              styles['--title-lines-overflow']
                ? classNames('title-ellipsis')
                : undefined
            }
            style={{
              ...(styles['--title-lines-overflow'] && {
                WebkitLineClamp: styles['--title-lines-overflow'],
              }),
              ...titleStyles,
            }}>
            {item.fields.title}
          </h4>
          {Boolean(item?.fields?.links?.length || item?.fields?.infoModal) && (
            <Actions>
              {item.fields.infoModal ? (
                <CardInfoModal
                  cta={item.fields.links?.[0]}
                  handleTrack={handleTrack}
                  infoModal={item.fields.infoModal}
                />
              ) : (
                item.fields.links.map((link) => (
                  <Link
                    key={link.sys.id}
                    component={link}
                    refs={refs}
                    handleTrack={handleTrack}
                  />
                ))
              )}
            </Actions>
          )}
        </div>
      )}
    </Container>
  );
}

function CardTypeB({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeB');
  const [, styles] = useCustomStyle(item.fields.settings);
  const titleStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--title'))
      .map(([key, value]) => [key.replace('--title-', ''), value])
  );
  const subtitleStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--subtitle'))
      .map(([key, value]) => [key.replace('--subtitle-', ''), value])
  );
  const descriptionStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--description'))
      .map(([key, value]) => [key.replace('--description-', ''), value])
  );

  const containerStyles = getStylesByPrefix('--container-', styles);
  const assetStyles = getStylesByPrefix('--asset-', styles);
  const actionsContainerStyles = getStylesByPrefix(
    '--actions-container-',
    styles
  );

  const pricingStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--pricing'))
      .map(([key, value]) => [key.replace('--pricing-', ''), value])
  );
  const strikethroughPricingStyles = Object.fromEntries(
    Object.entries(styles)
      .filter(([key]) => key.startsWith('--strikethrough-pricing'))
      .map(([key, value]) => [
        key.replace('--strikethrough-pricing-', ''),
        value,
      ])
  );

  const pricingText = pricingStyles['text'] || '';
  const strikethroughPricingText = strikethroughPricingStyles['text'] || '';

  return (
    <div className={classNames('container')} style={containerStyles}>
      <HomepageAsset asset={item.fields.asset} style={assetStyles} />
      <div>
        {item.fields.subtitle && (
          <h5
            className={classNames('container__subtitle')}
            style={subtitleStyles}>
            {item.fields.subtitle}
          </h5>
        )}
        <h4 style={titleStyles}>{item.fields.title}</h4>
        <p style={descriptionStyles}>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </p>
        {pricingText && (
          <div className={classNames('pricing')}>
            <p className={classNames('price')} style={pricingStyles}>
              {pricingText}
            </p>
            <p
              className={classNames('strikethrough')}
              style={strikethroughPricingStyles}>
              {strikethroughPricingText}
            </p>
          </div>
        )}
        {Boolean(item?.fields?.links?.length) && (
          <Actions style={actionsContainerStyles}>
            {item.fields.links.map((link) => {
              return (
                <LinkWithStyles
                  key={link.sys.id}
                  component={link}
                  refs={refs}
                  handleTrack={handleTrack}
                />
              );
            })}
          </Actions>
        )}
      </div>
    </div>
  );
}

function LinkWithStyles({ component, refs, handleTrack }) {
  console.log({ component });
  const [, styles] = useCustomStyle(component.fields.settings);
  const linkStyle = getStylesByPrefix('--link-', styles);

  return (
    <Link
      component={component}
      refs={refs}
      handleTrack={handleTrack}
      style={linkStyle}
    />
  );
}

function CardTypeC({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeC');
  const [setRef] = useCustomStyle(item.fields.settings);

  return (
    <div className={classNames('container')} ref={setRef}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      <div>
        <span>
          <HomepageImage field={item.fields.icon} /> {item.fields.subtitle}
        </span>
        <h4>{item.fields.title}</h4>
        <p>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </p>
        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                link={link}
                refs={refs}
                handleTrack={handleTrack}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeD({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeD');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      <div>
        <span>
          <HomepageImage field={item.fields.icon} /> {item.fields.subtitle}
        </span>
        <h4>{item.fields.title}</h4>
        <p>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </p>
        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => {
              const [styles] = useCustomStyle(link.fields.settings);

              return (
                <Link
                  key={link.sys.id}
                  component={link}
                  refs={refs}
                  handleTrack={handleTrack}
                  style={getStylesByPrefix('--link-', styles)}
                />
              );
            })}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeE({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeE');

  const [setRef, styles] = useCustomStyle(item.fields.settings);

  const hasLink = Boolean(item?.fields?.links?.length);
  const iconStyle = {
    ...getStylesByPrefix('--icon-', styles),
  };

  const containerStyle = {
    ...(styles['--height'] && {
      height: styles['--height'],
      width: styles['--height'].replace('px', '') * 0.8 + 'px',
    }),
  };

  if (!hasLink) {
    return (
      <div
        className={classNames('container')}
        ref={setRef}
        style={containerStyle}>
        <HomepageAsset
          asset={item.fields.asset}
          settings={item.fields.settings}
        />
        {item?.fields?.icon && (
          <div style={iconStyle}>
            <HomepageImage field={item.fields.icon} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames('container')}
      ref={setRef}
      style={containerStyle}>
      <Link
        component={item.fields.links[0]}
        refs={refs}
        handleTrack={handleTrack}>
        <HomepageAsset asset={item.fields.asset} />
      </Link>
      {item?.fields?.icon && (
        <div style={iconStyle}>
          <HomepageImage field={item.fields.icon} />
        </div>
      )}
    </div>
  );
}

function CardTypeF({ item }) {
  const classNames = useClassName('CLPCardTypeF');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      <ContentfulRichText field={{ 'en-US': item.fields.description }} />
      <span>
        {item.fields.subtitle}
        <HomepageImage field={item.fields.icon} />
      </span>
    </div>
  );
}

function CardTypeG({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeG');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      {Boolean(item?.fields?.links?.length) && (
        <Link
          component={item.fields.links[0]}
          refs={refs}
          handleTrack={handleTrack}
        />
      )}
    </div>
  );
}

function CardTypeH({ item, handleTrack }) {
  // Used for Avenue Store Cards
  const classNames = useClassName('CLPCardTypeH');
  const [setRef] = useCustomStyle(item.fields.settings);

  const handleClick = () => {
    if (item?.fields?.links?.length) {
      const link = item.fields.links[0];
      if (handleTrack) {
        handleTrack({
          event: STANDARD_EVENTS.click,
          params: {
            cta_type: CTA_TYPES.link,
            destination_url: link.fields.url,
          },
        });
      }
      window.location.href = link.fields.url;
    }
  };

  return (
    <div
      ref={setRef}
      className={classNames('container')}
      onClick={item?.fields?.links?.length ? handleClick : undefined}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      {item.fields.logoOverlay && (
        <div className={classNames('logo-overlay')}>
          <HomepageImage field={item.fields.logoOverlay} />
        </div>
      )}
      {item.fields.title && (
        <div className={classNames('info')}>
          <div>{item.fields.title}</div>
          <div>
            <HomepageImage field={item.fields.icon} />
            {item.fields.subtitle}
          </div>
        </div>
      )}
      {item.fields.badges?.length && (
        <ul>
          {item.fields.badges.map((badge) => (
            <Badge key={badge.sys.id}>{badge}</Badge>
          ))}
        </ul>
      )}
    </div>
  );
}

function CardTypeI({ item, refs, handleTrack }) {
  // Used for Avenue Life in Luxury Carousel Cards (Ferrari, Art, etc..)
  const classNames = useClassName('CLPCardTypeI');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
        className={classNames('image')}
      />
      <div className={classNames('gradient')} />
      <div className={classNames('content')}>
        <div className={classNames('subtitle')}>{item.fields.subtitle}</div>
        <div className={classNames('title')}>{item.fields.title}</div>
        <div className={classNames('description')}>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </div>

        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                component={link}
                refs={refs}
                handleTrack={handleTrack}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeJ({ item, handleTrack }) {
  // Used in Avenue Dining Cards (Carpaccio, Isola, etc..)
  const classNames = useClassName('CLPCardTypeJ');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      {Boolean(item?.fields?.title || item?.fields?.links?.length) && (
        <div>
          <h4>{item.fields.title}</h4>
          {Boolean(item?.fields?.links?.length) && (
            <Actions>
              {item.fields.links.map((link) => (
                <Link
                  key={link.sys.id}
                  component={link}
                  handleTrack={handleTrack}
                />
              ))}
            </Actions>
          )}
        </div>
      )}
    </div>
  );
}

function CardTypeK({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeK');

  return (
    <div className={classNames('container')}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      <div>
        <h4>{item.fields.title}</h4>
        {item.fields.labels && <Labels labels={item.fields.labels} />}
        <section>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </section>
        {Boolean(item?.fields?.infoModal) && (
          <InfoModal component={item?.fields?.infoModal} />
        )}
        {Boolean(item?.fields?.links?.length) && (
          <Actions>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                component={link}
                refs={refs}
                handleTrack={handleTrack}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeO({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeO');
  const [setRef] = useCustomStyle(item.fields.settings);

  return (
    <div className={classNames('container')} ref={setRef}>
      <HomepageAsset
        asset={item.fields.asset}
        settings={item.fields.settings}
      />
      <div>
        <div>
          <span>{item.fields.subtitle}</span>
          <h4>{item.fields.title}</h4>
          <p>
            <ContentfulRichText field={{ 'en-US': item.fields.description }} />
          </p>
          {item.fields?.labels?.length && (
            <ul>
              <li>
                {item.fields?.icon ? (
                  <HomepageImage field={item.fields.icon} />
                ) : null}
                {item.fields.labels[0]}
              </li>
            </ul>
          )}
        </div>

        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                component={link}
                refs={refs}
                handleTrack={handleTrack}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeP({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeP');
  const [setRef] = useCustomStyle(item.fields.settings);

  return (
    <div className={classNames('container')} ref={setRef}>
      <div>
        <HomepageAsset
          asset={item.fields.asset}
          settings={item.fields.settings}
        />
        <div>
          {item.fields.icon && <HomepageImage field={item.fields.icon} />}
        </div>
      </div>
      <div>
        <h5>{item.fields.subtitle}</h5>
        <h4>{item.fields.title}</h4>
        <p>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </p>
        {item.fields.badges?.length && (
          <ul>
            {item.fields.badges.map((badge) => (
              <Badge key={badge.sys.id}>{badge}</Badge>
            ))}
          </ul>
        )}
        <div className={classNames('footer')}>
          {item.fields.labels?.length && (
            <ul>
              {item.fields.labels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
          {Boolean(item?.fields?.links?.length) && (
            <Actions>
              {item.fields.links.map((link) => (
                <Link
                  key={link.sys.id}
                  component={link}
                  refs={refs}
                  handleTrack={handleTrack}
                />
              ))}
            </Actions>
          )}
        </div>
      </div>
    </div>
  );
}

function CardTypeQ({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeQ');
  const [setRef, style] = useCustomStyle(item.fields.settings);
  const asset = item.fields.asset;
  const ctaStyle = getStylesByPrefix('--cta-', style);

  const containerStyle = {
    // Check if height or min-height is set in styles, if not fallback to 100% or auto
    ...(style['--height'] && { height: style['--height'] }),
    ...(style['--min-height'] && { minHeight: style['--min-height'] }),
    ...(style['--background'] && { background: style['--background'] }),
    ...(style['--border-radius'] && { borderRadius: style['--border-radius'] }),
    ...(style['--padding'] && { padding: style['--padding'] }),
  };

  const titleStyles = {
    ...(style['--title-color'] && { color: style['--title-color'] }),
    ...(style['--title-font-size'] && { fontSize: style['--title-font-size'] }),
    ...(style['--title-font-weight'] && {
      fontWeight: style['--title-font-weight'],
    }),
    ...(style['--title-text-transform'] && {
      textTransform: style['--title-text-transform'],
    }),
  };

  const descriptionStyles = {
    ...(style['--description-color'] && {
      color: style['--description-color'],
    }),
    ...(style['--description-font-size'] && {
      fontSize: style['--description-font-size'],
    }),
    ...(style['--description-font-weight'] && {
      fontWeight: style['--description-font-weight'],
    }),
    ...(style['--description-text-transform'] && {
      textTransform: style['--description-text-transform'],
    }),
  };

  return (
    <div
      className={classNames(['container', asset && 'with-image'])}
      ref={setRef}
      {...(style?.background && {
        style: {
          '--image-gradient': `linear-gradient(to right, rgba(112, 121, 134, 0), ${style?.background})`,
          ...containerStyle,
        },
      })}>
      {asset && (
        <HomepageAsset
          asset={item.fields.asset}
          settings={item.fields.settings}
          videoProps={parseVideoSettings(style)}
        />
      )}
      <div>
        {item.fields.icon && <HomepageImage field={item.fields.icon} />}
        {item.fields.title && <h4 style={titleStyles}>{item.fields.title}</h4>}
        <p style={descriptionStyles}>
          <ContentfulRichText field={{ 'en-US': item.fields.description }} />
        </p>
        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                component={link}
                refs={refs}
                handleTrack={handleTrack}
                style={ctaStyle}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

function CardTypeR({ item, handleTrack }) {
  const classNames = useClassName('CLPCardTypeR');
  const [setRef, style] = useCustomStyle(item.fields.settings);
  const asset = item.fields.asset;

  const containerStyle = {
    ...(style['--height'] && { height: style['--height'] }),
    ...(style['--min-height'] && { minHeight: style['--min-height'] }),
    ...(style['--background'] && { background: style['--background'] }),
    ...(style['--border-radius'] && { borderRadius: style['--border-radius'] }),
  };

  const titleStyles = {
    ...(style['--title-color'] && { color: style['--title-color'] }),
    ...(style['--title-font-size'] && { fontSize: style['--title-font-size'] }),
    ...(style['--title-font-weight'] && {
      fontWeight: style['--title-font-weight'],
    }),
    ...(style['--title-text-transform'] && {
      textTransform: style['--title-text-transform'],
    }),
  };

  return (
    <div
      className={classNames(['container'])}
      style={containerStyle}
      ref={setRef}>
      {asset && (
        <HomepageAsset
          asset={item.fields.asset}
          settings={item.fields.settings}
        />
      )}
      <div className={classNames('content')}>
        {item.fields.title && (
          <h4 className={classNames('title')} style={titleStyles}>
            {item.fields.title}
          </h4>
        )}

        {Boolean(item?.fields?.links?.length) && (
          <Actions className={classNames('actions')}>
            {item.fields.links.map((link) => (
              <Link
                key={link.sys.id}
                component={link}
                handleTrack={handleTrack}
              />
            ))}
          </Actions>
        )}
      </div>
    </div>
  );
}

export default function Card({
  item,
  venue,
  refs,
  handleTrack,
  isActive,
  onSelect,
}) {
  const type = item.fields.type || 'Type A';

  const Component = {
    'Type A': CardTypeA,
    'Type B': CardTypeB,
    'Type C': CardTypeC,
    'Type D': CardTypeD,
    'Type E': CardTypeE,
    'Type F': CardTypeF,
    'Type G': CardTypeG,
    'Type H': CardTypeH,
    'Type I': CardTypeI,
    'Type J': CardTypeJ,
    'Type K': CardTypeK,
    'Type L': CardTypeL,
    'Type M': CardTypeM,
    'Type N': CardTypeN,
    'Type O': CardTypeO,
    'Type P': CardTypeP,
    'Type Q': CardTypeQ,
    'Type R': CardTypeR,
    'Type S': CardTypeS,
    'Type U': CardTypeU,
    'Type V': CardTypeV,
  }[type];

  if (!Component) {
    // A card configured in Contentful with an unsupported type would render as
    // `undefined` and crash the whole landing page. Skip it gracefully instead.
    console.warn(
      `[CustomLandingPage] Unsupported card type "${type}" (entry ${item?.sys?.id}); skipping card.`
    );
    return null;
  }

  return (
    <Component
      item={item}
      venue={venue}
      refs={refs}
      handleTrack={handleTrack}
      isActive={isActive}
      onSelect={onSelect}
    />
  );
}
